import React, { useState, useRef } from 'react';
import { 
  Scale, FileText, Search, Plus, CheckCircle2, AlertCircle, 
  Send, ChevronRight, History, Download, Copy, ArrowLeft, 
  Loader2, Gavel, ShieldCheck, FileSearch, Phone, Facebook
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Markdown from 'react-markdown';
import { cn } from './lib/utils';
import { processLegalRequest } from './services/geminiService';
type DocumentType = 'claim' | 'contract' | 'petition';
type ActionType = 'draft' | 'review';
type View = 'landing' | 'draft' | 'review' | 'result';

export default function App() {
  const [view, setView] = useState<View>('landing');
  const [docType, setDocType] = useState<DocumentType>('claim');
  const [action, setAction] = useState<ActionType>('draft');
  const [input, setInput] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<{title: string, date: string, content: string}[]>([]);

  const resultRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async () => {
    if (!input.trim()) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await processLegalRequest({
        docType: docType,
        action: action,
        input: input
      });
      setResult(response || "Хариу ирсэнгүй.");
      setView('result');
      
      const typeLabel = docType === 'claim' ? 'Нэхэмжлэл' : docType === 'petition' ? 'Хүсэлт' : 'Гэрээ';
      const title = action === 'draft' ? `${typeLabel} боловсруулах` : `${typeLabel} хянах`;
      
      setHistory(prev => [{
        title,
        date: new Date().toLocaleString(),
        content: response || ""
      }, ...prev.slice(0, 9)]);
      
    } catch (err: any) {
      setError(err.message || "Алдаа гарлаа.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(result);
    }
  };

  const renderLanding = () => (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <div className="text-center mb-16">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="inline-flex items-center justify-center p-0 bg-white rounded-2xl mb-6 shadow-xl overflow-hidden border-4 border-legal-primary"
        >
          <img 
            src="/mainlogo.png" 
            alt="Logo" 
            className="w-32 h-32 object-contain"
           
            referrerPolicy="no-referrer"
          />
        </motion.div>
        <motion.h1 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight"
        >
          Anar and Zol
        </motion.h1>
        <motion.p 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-xl text-slate-600 max-w-2xl mx-auto"
        >
          Шүүхэд гаргах нэхэмжлэл, гэрээ боловсруулах болон хууль зүйн зөвлөгөө өгөх ухаалаг систем
        </motion.p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Нэхэмжлэл */}
        <motion.div whileHover={{ y: -5 }} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex flex-col h-full">
          <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6">
            <Gavel size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-4">Нэхэмжлэл</h2>
          <p className="text-slate-600 mb-8 flex-grow">Шүүхэд гаргах нэхэмжлэлийг хууль тогтоомжийн дагуу мэргэжлийн түвшинд боловсруулах.</p>
          <div className="space-y-3">
            <button onClick={() => { setDocType('claim'); setAction('draft'); setView('draft'); }} className="w-full py-4 px-6 bg-legal-primary text-white rounded-xl font-semibold flex items-center justify-between hover:bg-legal-secondary transition-colors">Боловсруулах <Plus size={20} /></button>
            <button onClick={() => { setDocType('claim'); setAction('review'); setView('review'); }} className="w-full py-4 px-6 bg-white text-legal-primary border-2 border-legal-primary rounded-xl font-semibold flex items-center justify-between hover:bg-slate-50 transition-colors">Хянуулах <FileSearch size={20} /></button>
          </div>
        </motion.div>

        {/* Хүсэлт */}
        <motion.div whileHover={{ y: -5 }} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex flex-col h-full">
          <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-6">
            <FileText size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-4">Хүсэлт, гомдол</h2>
          <p className="text-slate-600 mb-8 flex-grow">Шүүх болон бусад байгууллагад гаргах хүсэлт, гомдол, албан бичиг боловсруулах.</p>
          <div className="space-y-3">
            <button onClick={() => { setDocType('petition'); setAction('draft'); setView('draft'); }} className="w-full py-4 px-6 bg-legal-primary text-white rounded-xl font-semibold flex items-center justify-between hover:bg-legal-secondary transition-colors">Боловсруулах <Plus size={20} /></button>
            <button onClick={() => { setDocType('petition'); setAction('review'); setView('review'); }} className="w-full py-4 px-6 bg-white text-legal-primary border-2 border-legal-primary rounded-xl font-semibold flex items-center justify-between hover:bg-slate-50 transition-colors">Хянуулах <FileSearch size={20} /></button>
          </div>
        </motion.div>

        {/* Гэрээ */}
        <motion.div whileHover={{ y: -5 }} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex flex-col h-full">
          <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-6">
            <ShieldCheck size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-4">Гэрээ</h2>
          <p className="text-slate-600 mb-8 flex-grow">Бүх төрлийн гэрээ, хэлцэл боловсруулах, эрсдэлийг хянах, хууль зүйн дүгнэлт авах.</p>
          <div className="space-y-3">
            <button onClick={() => { setDocType('contract'); setAction('draft'); setView('draft'); }} className="w-full py-4 px-6 bg-legal-primary text-white rounded-xl font-semibold flex items-center justify-between hover:bg-legal-secondary transition-colors">Боловсруулах <Plus size={20} /></button>
            <button onClick={() => { setDocType('contract'); setAction('review'); setView('review'); }} className="w-full py-4 px-6 bg-white text-legal-primary border-2 border-legal-primary rounded-xl font-semibold flex items-center justify-between hover:bg-slate-50 transition-colors">Хянуулах <FileSearch size={20} /></button>
          </div>
        </motion.div>
      </div>

      {history.length > 0 && (
        <div className="mt-20">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2"><History size={24} /> Сүүлийн үйлдлүүд</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {history.map((item, i) => (
              <button key={i} onClick={() => { setResult(item.content); setView('result'); }} className="text-left p-4 bg-white rounded-xl border border-slate-100 hover:border-legal-primary transition-all group">
                <div className="font-semibold group-hover:text-legal-primary transition-colors">{item.title}</div>
                <div className="text-xs text-slate-400 mt-1">{item.date}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderInputForm = () => (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <button onClick={() => setView('landing')} className="flex items-center gap-2 text-slate-500 hover:text-legal-primary mb-8 transition-colors"><ArrowLeft size={20} /> Буцах</button>
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
        <div className="bg-legal-primary p-8 text-white">
          <h2 className="text-2xl font-bold mb-2">{action === 'draft' ? 'Шинээр боловсруулах' : 'Хянуулах, зөвлөмж авах'}</h2>
          <p className="opacity-80">{docType === 'claim' ? 'Шүүхэд гаргах нэхэмжлэл' : docType === 'petition' ? 'Хүсэлт, гомдол' : 'Гэрээ, хэлцэл'}</p>
        </div>
        <div className="p-8">
          <textarea 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full h-64 p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-legal-primary outline-none transition-all resize-none"
            placeholder="Дэлгэрэнгүй мэдээллээ энд оруулна уу..."
          />
          {error && <div className="mt-4 p-4 bg-red-50 text-red-600 rounded-xl flex items-start gap-3"><AlertCircle size={20} /><p className="text-sm">{error}</p></div>}
          <div className="mt-8 flex justify-end">
            <button onClick={handleSubmit} disabled={loading || !input.trim()} className={cn("py-4 px-10 rounded-xl font-bold flex items-center gap-3 transition-all shadow-lg", loading || !input.trim() ? "bg-slate-200 text-slate-400" : "bg-legal-primary text-white hover:bg-legal-secondary")}>
              {loading ? <>Боловсруулж байна... <Loader2 className="animate-spin" size={20} /></> : <>Илгээх <Send size={20} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderResult = () => (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <button onClick={() => setView(action === 'draft' ? 'draft' : 'review')} className="flex items-center gap-2 text-slate-500 hover:text-legal-primary transition-colors"><ArrowLeft size={20} /> Засах</button>
        <div className="flex items-center gap-3">
          <button onClick={copyToClipboard} className="p-3 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-2 font-semibold text-slate-700"><Copy size={20} /> Хуулах</button>
          <button onClick={() => window.print()} className="p-3 bg-legal-primary text-white rounded-xl hover:bg-legal-secondary transition-colors flex items-center gap-2 font-semibold shadow-lg"><Download size={20} /> Хэвлэх / PDF</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            <div className="p-8 md:p-12 prose prose-slate max-w-none" ref={resultRef}>
              <Markdown>{result || ""}</Markdown>
            </div>
          </div>
        </div>
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-amber-50 rounded-3xl p-8 border border-amber-100">
            <h3 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2"><AlertCircle size={24} /> Санамж</h3>
            <p className="text-sm text-amber-800">Энэхүү баримт бичиг нь AI-аар боловсруулагдсан тул та заавал хянаж үзнэ үү.</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen font-sans">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
          <button onClick={() => setView('landing')} className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-xl shadow-lg overflow-hidden border border-slate-100">
              <img src="/mainlogo.png" alt="Logo" className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80&w=100&h=100"; }} />
            </div>
            <span className="text-xl font-bold text-slate-900">Anar and Zol</span>
          </button>
          <nav className="hidden md:flex items-center gap-8 font-medium">
            <button onClick={() => setView('landing')} className="text-slate-600 hover:text-legal-primary">Нүүр</button>
            <a href="tel:95178868" className="text-legal-primary flex items-center gap-2"><Phone size={18} /> 95178868</a>
          </nav>
        </div>
      </header>

      <main>
        <AnimatePresence mode="wait">
          <motion.div key={view} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {view === 'landing' && renderLanding()}
            {(view === 'draft' || view === 'review') && renderInputForm()}
            {view === 'result' && renderResult()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}